import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  newPartialJsonState,
  appendDelta,
  type ParserState,
} from '@/lib/partial-json';
import type {
  SSEEvent,
  ToolName,
  PlanStep,
  SummarySection,
  SuggestionChips,
} from '@arcanum-hq/shared-types';
import type {
  StreamingMessage,
  StreamingStatus,
  ToolCallState,
} from './use-streaming-v2';

/**
 * `deal-signal-overhaul` Phase 5 Session 3 (2026-05-13).
 *
 * Public deal-link chat SSE consumer. Mirrors the auth-side
 * `use-streaming-v2.ts` reducer (20 SSE variants → `StreamingMessage`
 * shape) but with the public-chat-specific shape:
 *
 *   - Inline `fetch` (NOT `api.stream`) with `credentials: 'include'`
 *     baked in so the server-minted `viewer_session_id` cookie round-
 *     trips on the cross-origin (`:5173 → :3001`) boundary. Without
 *     `credentials: 'include'` the cookie does NOT attach, viewer
 *     attribution + cookie-bound rate limits fail silently. `api.stream`
 *     does not currently expose a credentials option — inlining is the
 *     least-invasive fix (see research § 11 api.stream gap).
 *   - On `done`: invalidates `['public-deal-conversation', shareId]` so
 *     `usePublicDealConversation` re-fetches the persisted turn.
 *   - NO server-side abort endpoint — the public agent is stateless
 *     per-turn (Phase 3 Session 4). Local abort clears `currentTurn`
 *     synchronously + schedules a deferred 1500ms refetch so the
 *     eventually-persisted turn appears without an extra fetch round-
 *     trip if the user moves on.
 *   - Reducer is duplicated (not extracted) from `use-streaming-v2`
 *     because auth-side handles `missing_model` + `security_block`
 *     differently; revisit a shared helper only after drift never
 *     materializes (Session 3 implementation step 2).
 *
 * Optimistic stub retention: `currentTurn` stays mounted UNTIL the
 * refetched `usePublicDealConversation` returns the persisted turn
 * (turn-count increment). The wrapper component (`PublicDealChatThread`
 * + the Session 4 route wrapper) owns the cross-check via the
 * `pendingTurnIdRef`-style mechanism; this hook clears `currentTurn`
 * on the next `sendMessage` start OR on `abort()`.
 *
 * The auth-side `tool_input_*` partial-JSON accumulator is reused via
 * the same `partialJsonStatesRef` keyed by `tool_call_id`. Each
 * `sendMessage` invocation resets the parser map (per-message-body
 * locality).
 */

export interface UseSharedStreamingReturn {
  status: StreamingStatus;
  currentTurn: StreamingMessage | null;
  error: string | null;
  sendMessage: (content: string, overrides?: Record<string, unknown>) => void;
  abort: () => void;
  /**
   * Optimistic echo of the visitor's just-submitted question (typed OR
   * suggestion-chip), set synchronously in `sendMessage` so the human bubble
   * renders IMMEDIATELY — before/while the agent reasons — instead of only
   * after the `done`→refetch lands the persisted human turn (~30s). Transient
   * client-only string: never persisted, never on the wire, never injected
   * into the conversation cache (a +1 turns.length write would trip
   * `<PublicDealChatThread>`'s stub-replacement gate and vanish the answer).
   * Set in `sendMessage` (covers rapid re-submit by overwrite), cleared in
   * `abort`, and RETAINED through `done`/`error`/`security_block` so the bubble
   * stays visible across the done→refetch window; the consumer suppresses it
   * once the persisted human turn arrives (co-gated on `showStreamingStub`).
   */
  pendingHuman: string | null;
  /**
   * Recomputed suggestion chips pushed on the terminal `done` SSE event
   * (public-deal-link-polish WS2, ADR 0003 — additive `done.suggestionChips`).
   * `null` until the first post-turn recompute lands. The wrapper merges this
   * over the turn-0 seed (`usePublicDeal().data.suggestionChips`) so the
   * NarrativeBar always shows the freshest set. Reset to `null` at each new
   * `sendMessage` + on `abort` so a stale set never flashes across turns.
   */
  recomputedChips: SuggestionChips | null;
}

const PUBLIC_DEAL_CONVERSATION_KEY = (shareId: string) =>
  ['public-deal-conversation', shareId] as const;

function initialStreamingMessage(): StreamingMessage {
  return {
    id: `pub-msg-${Date.now()}`,
    role: 'ai',
    content: '',
    toolResults: [],
    toolErrors: [],
    redactedSpans: [],
    activeToolCall: null,
    isStreaming: true,
    toolCallStates: new Map(),
    thinking: null,
    plan: null,
    summary: null,
    abortedReason: null,
  };
}

function findLastEntry<T extends ToolCallState>(
  states: Map<string, ToolCallState>,
  predicate: (state: ToolCallState) => state is T
): { id: string; state: T } | null {
  let last: { id: string; state: T } | null = null;
  for (const [id, state] of states) {
    if (predicate(state)) last = { id, state };
  }
  return last;
}

const isInputReady = (
  s: ToolCallState
): s is Extract<ToolCallState, { phase: 'input_ready' }> =>
  s.phase === 'input_ready';
const isExecuting = (
  s: ToolCallState
): s is Extract<ToolCallState, { phase: 'executing' }> =>
  s.phase === 'executing';
const isPendingTerminal = (
  s: ToolCallState
): s is Extract<
  ToolCallState,
  { phase: 'input_streaming' | 'input_ready' | 'executing' }
> =>
  s.phase === 'input_streaming' ||
  s.phase === 'input_ready' ||
  s.phase === 'executing';

function deriveActiveToolCall(
  states: Map<string, ToolCallState>
): ToolName | null {
  const entry = findLastEntry(states, isExecuting);
  return entry?.state.tool ?? null;
}

const API_URL = import.meta.env.VITE_API_URL || '';

async function getOptionalAuthHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export function useSharedStreaming(shareId: string): UseSharedStreamingReturn {
  const [status, setStatus] = useState<StreamingStatus>('idle');
  const [currentTurn, setCurrentTurn] = useState<StreamingMessage | null>(null);
  const [pendingHuman, setPendingHuman] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recomputedChips, setRecomputedChips] =
    useState<SuggestionChips | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const partialJsonStatesRef = useRef<Map<string, ParserState>>(new Map());
  const queryClient = useQueryClient();

  // Cleanup on unmount — abort any in-flight stream so unmount during
  // streaming doesn't leak a fetch.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const handleEvent = useCallback(
    (sse: SSEEvent) => {
      setStatus('streaming');

      switch (sse.type) {
        case 'token':
          setCurrentTurn((prev) =>
            prev ? { ...prev, content: prev.content + sse.content } : prev
          );
          break;

        // ---------- Reasoning lifecycle ----------

        case 'thinking_start':
          setCurrentTurn((prev) =>
            prev
              ? {
                  ...prev,
                  thinking: {
                    startedAt: Date.now(),
                    durationMs: null,
                    tokenCount: null,
                    text: '',
                  },
                }
              : prev
          );
          break;

        case 'thinking_delta':
          setCurrentTurn((prev) =>
            prev?.thinking
              ? {
                  ...prev,
                  thinking: {
                    ...prev.thinking,
                    text: prev.thinking.text + sse.text,
                  },
                }
              : prev
          );
          break;

        case 'thinking_stop':
          setCurrentTurn((prev) =>
            prev?.thinking
              ? {
                  ...prev,
                  thinking: {
                    ...prev.thinking,
                    durationMs: sse.duration_ms,
                    tokenCount: sse.token_count ?? null,
                  },
                }
              : prev
          );
          break;

        // ---------- Tool input lifecycle ----------

        case 'tool_input_start': {
          const id = sse.tool_call_id;
          partialJsonStatesRef.current.set(id, newPartialJsonState());
          setCurrentTurn((prev) => {
            if (!prev) return prev;
            const next = new Map(prev.toolCallStates);
            next.set(id, {
              phase: 'input_streaming',
              tool: sse.tool,
              partialInput: {},
              rawJson: '',
            });
            return { ...prev, toolCallStates: next };
          });
          break;
        }

        case 'tool_input_delta': {
          const id = sse.tool_call_id;
          const parserState =
            partialJsonStatesRef.current.get(id) ?? newPartialJsonState();
          const { state: nextParser, parsed } = appendDelta(
            parserState,
            sse.partial_input_json
          );
          partialJsonStatesRef.current.set(id, nextParser);
          setCurrentTurn((prev) => {
            if (!prev) return prev;
            const existing = prev.toolCallStates.get(id);
            if (!existing || existing.phase !== 'input_streaming') return prev;
            const next = new Map(prev.toolCallStates);
            next.set(id, {
              phase: 'input_streaming',
              tool: existing.tool,
              partialInput: parsed,
              rawJson: nextParser.rawJson,
            });
            return { ...prev, toolCallStates: next };
          });
          break;
        }

        case 'tool_input_ready': {
          const id = sse.tool_call_id;
          partialJsonStatesRef.current.delete(id);
          setCurrentTurn((prev) => {
            if (!prev) return prev;
            const existing = prev.toolCallStates.get(id);
            if (!existing) return prev;
            const next = new Map(prev.toolCallStates);
            next.set(id, {
              phase: 'input_ready',
              tool: existing.tool,
              input: sse.input,
            });
            return { ...prev, toolCallStates: next };
          });
          break;
        }

        // ---------- Tool execution ----------

        case 'tool_start':
          setCurrentTurn((prev) => {
            if (!prev) return prev;
            const matched = findLastEntry(
              prev.toolCallStates,
              (s): s is Extract<ToolCallState, { phase: 'input_ready' }> =>
                isInputReady(s) && s.tool === sse.tool
            );
            const next = new Map(prev.toolCallStates);
            if (matched) {
              next.set(matched.id, {
                phase: 'executing',
                tool: matched.state.tool,
                input: matched.state.input,
                progress: [],
              });
            } else {
              const fallbackId = `legacy-live-${sse.tool}-${Date.now()}`;
              next.set(fallbackId, {
                phase: 'executing',
                tool: sse.tool,
                input: {},
                progress: [],
              });
            }
            return {
              ...prev,
              toolCallStates: next,
              activeToolCall: deriveActiveToolCall(next),
            };
          });
          break;

        case 'tool_progress': {
          const id = sse.tool_call_id;
          setCurrentTurn((prev) => {
            if (!prev) return prev;
            const existing = prev.toolCallStates.get(id);
            if (!existing || existing.phase !== 'executing') return prev;
            const next = new Map(prev.toolCallStates);
            next.set(id, {
              ...existing,
              progress: [
                ...existing.progress,
                { phase: sse.phase, payload: sse.payload },
              ],
            });
            return { ...prev, toolCallStates: next };
          });
          break;
        }

        case 'tool_result':
          setCurrentTurn((prev) => {
            if (!prev) return prev;
            const matched = findLastEntry(
              prev.toolCallStates,
              (s): s is Extract<ToolCallState, { phase: 'executing' }> =>
                isExecuting(s) && s.tool === sse.tool
            );
            const next = new Map(prev.toolCallStates);
            if (matched) {
              next.set(matched.id, {
                phase: 'complete',
                tool: matched.state.tool,
                input: matched.state.input,
                result: sse.result,
              });
            } else {
              const fallbackId = `legacy-live-${sse.tool}-${Date.now()}`;
              next.set(fallbackId, {
                phase: 'complete',
                tool: sse.tool,
                input: {},
                result: sse.result,
              });
            }
            return {
              ...prev,
              activeToolCall: deriveActiveToolCall(next),
              toolResults: [
                ...prev.toolResults,
                { tool: sse.tool, result: sse.result, timestamp: Date.now() },
              ],
              toolCallStates: next,
            };
          });
          break;

        case 'tool_error':
          setCurrentTurn((prev) => {
            if (!prev) return prev;
            const matched = findLastEntry(
              prev.toolCallStates,
              (s): s is Extract<ToolCallState, { phase: 'executing' }> =>
                isExecuting(s) && s.tool === sse.tool
            );
            const next = new Map(prev.toolCallStates);
            if (matched) {
              next.set(matched.id, {
                phase: 'errored',
                tool: matched.state.tool,
                input: matched.state.input,
                error: {
                  code: sse.code,
                  message: sse.message,
                  details: sse.details,
                },
              });
            } else {
              const fallbackId = `legacy-live-${sse.tool}-${Date.now()}`;
              next.set(fallbackId, {
                phase: 'errored',
                tool: sse.tool,
                input: null,
                error: {
                  code: sse.code,
                  message: sse.message,
                  details: sse.details,
                },
              });
            }
            const nextPlan =
              prev.plan && prev.plan.activeStepId
                ? { ...prev.plan, erroredStepId: prev.plan.activeStepId }
                : prev.plan;
            return {
              ...prev,
              activeToolCall: deriveActiveToolCall(next),
              toolErrors: [
                ...prev.toolErrors,
                {
                  tool: sse.tool,
                  code: sse.code,
                  message: sse.message,
                  details: sse.details,
                  timestamp: Date.now(),
                },
              ],
              toolCallStates: next,
              plan: nextPlan,
            };
          });
          break;

        // ---------- Plan lifecycle ----------

        case 'plan_proposed':
          setCurrentTurn((prev) =>
            prev
              ? {
                  ...prev,
                  plan: {
                    steps: sse.steps as PlanStep[],
                    activeStepId: null,
                    completedStepIds: new Set(),
                    erroredStepId: null,
                  },
                }
              : prev
          );
          break;

        case 'plan_step_active':
          setCurrentTurn((prev) =>
            prev?.plan
              ? { ...prev, plan: { ...prev.plan, activeStepId: sse.step_id } }
              : prev
          );
          break;

        case 'plan_step_complete':
          setCurrentTurn((prev) =>
            prev?.plan
              ? {
                  ...prev,
                  plan: {
                    ...prev.plan,
                    completedStepIds: new Set([
                      ...prev.plan.completedStepIds,
                      sse.step_id,
                    ]),
                    activeStepId:
                      prev.plan.activeStepId === sse.step_id
                        ? null
                        : prev.plan.activeStepId,
                  },
                }
              : prev
          );
          break;

        // ---------- Abort (server-emitted) ----------

        case 'aborted':
          setCurrentTurn((prev) => {
            if (!prev) return prev;
            const next = new Map(prev.toolCallStates);
            for (const [id, state] of next) {
              if (isPendingTerminal(state)) {
                next.set(id, {
                  phase: 'aborted',
                  tool: state.tool,
                  input: 'input' in state ? state.input : null,
                });
              }
            }
            return {
              ...prev,
              isStreaming: false,
              activeToolCall: null,
              abortedReason: sse.reason,
              toolCallStates: next,
            };
          });
          setStatus('aborted');
          break;

        // ---------- Summary ----------

        case 'summary':
          setCurrentTurn((prev) =>
            prev
              ? {
                  ...prev,
                  summary: { sections: sse.sections as SummarySection[] },
                }
              : prev
          );
          break;

        // ---------- Stream terminators ----------

        case 'done': {
          setCurrentTurn((prev) =>
            prev ? { ...prev, isStreaming: false, activeToolCall: null } : prev
          );
          setStatus('done');
          // WS2 (ADR 0003): consume the additive `done.suggestionChips` recompute.
          // Exactly-3 guard mirrors the server emit (sharing.service.ts only rides
          // the field when `sanitized.length === 3`) + the shared schema's
          // `.length(3)`; a non-3 / absent set leaves the prior chips untouched so
          // the bar never flashes a partial row. The wrapper merges this over the
          // turn-0 seed (`usePublicDeal().data.suggestionChips`).
          const fresh = (sse as { suggestionChips?: SuggestionChips })
            .suggestionChips;
          if (fresh && Array.isArray(fresh.chips) && fresh.chips.length === 3) {
            setRecomputedChips(fresh);
          }
          queryClient.invalidateQueries({
            queryKey: PUBLIC_DEAL_CONVERSATION_KEY(shareId),
          });
          break;
        }

        case 'error':
          setError(sse.message);
          setCurrentTurn((prev) =>
            prev ? { ...prev, isStreaming: false, activeToolCall: null } : prev
          );
          setStatus('error');
          break;

        case 'security_block':
          setError(`security_block: ${sse.reason}`);
          setCurrentTurn((prev) =>
            prev ? { ...prev, isStreaming: false, activeToolCall: null } : prev
          );
          setStatus('security_blocked');
          break;

        case 'security_redact':
          setCurrentTurn((prev) =>
            prev
              ? {
                  ...prev,
                  redactedSpans: [
                    ...prev.redactedSpans,
                    {
                      category: sse.category,
                      stage: sse.stage,
                      redactedSpan: sse.redacted_span,
                      replacement: sse.replacement,
                      timestamp: Date.now(),
                    },
                  ],
                }
              : prev
          );
          break;
      }
    },
    [queryClient, shareId]
  );

  const sendMessage = useCallback(
    (content: string, overrides?: Record<string, unknown>) => {
      // Tear down any prior in-flight stream BEFORE re-init — sequential
      // sends from the composer must not interleave reducer writes.
      abortRef.current?.abort();
      partialJsonStatesRef.current = new Map();
      setError(null);
      setStatus('connecting');
      setCurrentTurn(initialStreamingMessage());
      // Optimistic human echo — render the visitor's question as a bubble
      // immediately, in lockstep with the AI stub above. Overwriting on a
      // rapid second submit is correct (the prior turn is being torn down).
      setPendingHuman(content);
      // Drop the prior turn's recomputed chips so a stale set never flashes
      // while the new turn streams; the next `done` repopulates (WS2).
      setRecomputedChips(null);

      const controller = new AbortController();
      abortRef.current = controller;

      void (async () => {
        try {
          const authHeaders = await getOptionalAuthHeaders();
          const res = await fetch(
            `${API_URL}/api/shared/${shareId}/conversation/messages`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...authHeaders },
              credentials: 'include',
              body: JSON.stringify(
                overrides && Object.keys(overrides).length > 0
                  ? { message: content, overrides }
                  : { message: content }
              ),
              signal: controller.signal,
            }
          );

          if (!res.ok || !res.body) {
            let message = `Stream failed: ${res.status}`;
            try {
              const errJson = (await res.json()) as { message?: string };
              if (errJson.message) message = errJson.message;
            } catch {
              // body wasn't JSON — fall back to the status-line message.
            }
            setError(message);
            setStatus('error');
            setCurrentTurn((prev) =>
              prev
                ? { ...prev, isStreaming: false, activeToolCall: null }
                : prev
            );
            return;
          }

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';
            for (const line of lines) {
              const trimmed = line.trim();
              if (trimmed.startsWith('data: ')) {
                try {
                  const data = JSON.parse(trimmed.slice(6)) as SSEEvent;
                  handleEvent(data);
                } catch {
                  // skip malformed SSE lines
                }
              }
            }
          }
        } catch (err) {
          if ((err as Error)?.name === 'AbortError') {
            // Local abort — `abort()` already updated state synchronously.
            return;
          }
          const msg = err instanceof Error ? err.message : String(err);
          setError(msg);
          setStatus('error');
          setCurrentTurn((prev) =>
            prev ? { ...prev, isStreaming: false, activeToolCall: null } : prev
          );
        } finally {
          if (abortRef.current === controller) {
            abortRef.current = null;
          }
        }
      })();
    },
    [shareId, handleEvent]
  );

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus('idle');
    setCurrentTurn(null);
    // Symmetric with currentTurn — drop the optimistic human echo on abort.
    // The server still persists the human+agent rows; the deferred 1500ms
    // refetch below backfills them as persisted turns.
    setPendingHuman(null);
    setError(null);
    // No server-side abort endpoint — the public agent is stateless per
    // turn. The server completes its current generation and persists the
    // turn anyway; this deferred refetch picks it up without a tight
    // round-trip on the discard path.
    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: PUBLIC_DEAL_CONVERSATION_KEY(shareId),
      });
    }, 1500);
  }, [queryClient, shareId]);

  return {
    status,
    currentTurn,
    pendingHuman,
    error,
    sendMessage,
    abort,
    recomputedChips,
  };
}
